import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntercom } from 'react-use-intercom'
import {
  activateIntercom,
  useIntercomActivationStore,
} from '~/src/stores/useIntercomActivationStore'

interface Props {
  customAttributes: Record<string, any>
}

const useShowIntercom = ({ customAttributes }: Props) => {
  const [isIntercomOpen, setIsIntercomOpen] = useState(false)
  const { hide, isOpen, update, showNewMessage } = useIntercom()
  const booted = useIntercomActivationStore((s) => s.booted)

  const serialized = JSON.stringify(customAttributes)

  // Keep a stable ref so callbacks don't depend on the object identity
  const customAttributesRef = useRef(customAttributes)
  customAttributesRef.current = customAttributes

  // Track previous isOpen state to detect close transitions
  const wasOpenRef = useRef(false)

  // Intercom activates lazily (useIntercomActivationStore) and
  // react-use-intercom drops commands issued before boot, so pre-boot
  // open/enable calls are queued here and flushed once booted.
  // pendingOpenRef: undefined = none; null = open without prepopulated content
  const pendingOpenRef = useRef<string | null | undefined>(undefined)
  const pendingEnableRef = useRef(false)

  const showLauncher = useCallback(
    () =>
      update({
        hideDefaultLauncher: false,
        customAttributes: customAttributesRef.current,
      }),
    [update]
  )

  useEffect(() => {
    if (!booted) return
    if (pendingEnableRef.current) {
      pendingEnableRef.current = false
      showLauncher()
    }
    if (pendingOpenRef.current !== undefined) {
      const content = pendingOpenRef.current
      pendingOpenRef.current = undefined
      showLauncher()
      showNewMessage(content ?? undefined)
      setIsIntercomOpen(true)
    }
  }, [booted, showLauncher, showNewMessage])

  const openIntercom = useCallback(
    (prePopulatedContent?: string) => {
      if (!booted) {
        pendingOpenRef.current = prePopulatedContent ?? null
        activateIntercom()
        return
      }

      if (isOpen) return

      showLauncher()
      showNewMessage(prePopulatedContent)
      setIsIntercomOpen(true)
    },
    [booted, isOpen, showLauncher, showNewMessage]
  )

  const enableIntercom = useCallback(() => {
    if (!booted) {
      pendingEnableRef.current = true
      activateIntercom()
      return
    }
    showLauncher()
  }, [booted, showLauncher])

  // Re-hide the default launcher when the user closes the messenger,
  // so only our custom "Need help?" button remains visible.
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      update({ hideDefaultLauncher: true })
      setIsIntercomOpen(false)
    }
    wasOpenRef.current = isOpen
  }, [isOpen, update])

  // Automatically refresh Intercom context when customAttributes change
  useEffect(() => {
    if (!booted) return
    update({
      customAttributes: customAttributesRef.current,
    })
  }, [booted, update, serialized])

  // on tab close any open Intercom chat
  useEffect(() => {
    const handleBeforeUnload = () => {
      hide()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hide, isOpen])

  return {
    isIntercomOpen,
    enableIntercom,
    openIntercom,
    update,
  }
}

export default useShowIntercom
